import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertCircle, Loader2, ScanLine } from "lucide-solid";

import { useNavigate } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { resolveBarcode } from "@/features/catalog/data/catalog-rest";

/**
 * 🎯 Escáner de código de barras → EAN/UPC → resolución → ficha del producto.
 *
 * La resolución va por REST público (`/api/catalog/resolve/:code`), guest-first y
 * offline-tolerante. Se intenta abrir la cámara (getUserMedia) como preview; si
 * no hay permiso / no hay cámara (headless, e2e), degradamos limpiamente al
 * campo manual, que es la ruta fiable para tests y para teclear el código a mano.
 * Traducción del diseño Stitch `escaner_de_codigo_de_barras`.
 */
export function ScannerPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [code, setCode] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [notFound, setNotFound] = createSignal(false);
  const [cameraOn, setCameraOn] = createSignal(false);
  let videoEl: HTMLVideoElement | undefined;
  let stream: MediaStream | undefined;

  onMount(() => {
    // Preview de cámara best-effort. Nunca bloquea: el campo manual es la ruta
    // canónica y el único camino en headless/e2e.
    void (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (videoEl) {
          videoEl.srcObject = stream;
          setCameraOn(true);
        }
      } catch {
        setCameraOn(false);
      }
    })();
  });

  onCleanup(() => {
    for (const track of stream?.getTracks() ?? []) {
      track.stop();
    }
  });

  const handleResolve = async (raw: string) => {
    const value = raw.trim();
    if (!value) {
      return;
    }
    setBusy(true);
    setNotFound(false);
    try {
      const product = await resolveBarcode(value);
      if (product) {
        void navigate({ to: "/catalog/$productId", params: { productId: product.id } });
      } else {
        setNotFound(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex flex-col gap-6" data-testid="scanner-page">
      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Escanear`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Apunta la cámara al código de barras del set, o escribe el EAN/UPC.`)}
        </p>
      </header>

      {/* Visor */}
      <div class="relative grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-2xl border border-border/60 bg-foreground/90">
        <video
          ref={videoEl}
          autoplay
          playsinline
          muted
          class="absolute inset-0 h-full w-full object-cover"
          classList={{ hidden: !cameraOn() }}
        />
        <Show when={!cameraOn()}>
          <div class="flex flex-col items-center gap-2 text-background/80">
            <ScanLine class="h-12 w-12" />
            <p class="text-sm">{t(msg`Cámara no disponible — introduce el código abajo`)}</p>
          </div>
        </Show>
        {/* Guía de encuadre */}
        <div class="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-lg border-2 border-primary/80" />
      </div>

      {/* Entrada manual (ruta canónica) */}
      <form
        class="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void handleResolve(code());
        }}
      >
        <label class="text-xs font-bold uppercase tracking-wide text-muted-foreground" for="ean">
          {t(msg`Código de barras (EAN / UPC)`)}
        </label>
        <input
          id="ean"
          data-testid="scanner-code-input"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          value={code()}
          onInput={(e) => setCode(e.currentTarget.value)}
          placeholder={t(msg`p. ej. 5054131050231`)}
          class="w-full rounded-lg border-2 border-border bg-background px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none"
        />
        <button
          type="submit"
          data-testid="scanner-resolve"
          disabled={busy() || !code().trim()}
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-base font-semibold text-primary-foreground shadow-md transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          <Show when={busy()} fallback={<ScanLine class="h-5 w-5" />}>
            <Loader2 class="h-5 w-5 animate-spin" />
          </Show>
          {t(msg`Buscar producto`)}
        </button>
      </form>

      <Show when={notFound()}>
        <div
          data-testid="scanner-not-found"
          class="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          <AlertCircle class="h-5 w-5 shrink-0" />
          {t(msg`Ese código no está en el catálogo todavía.`)}
        </div>
      </Show>
    </div>
  );
}
